import React, { useState } from "react";
import Spinner from "../../../components/presentationals/Spinner";
import Error from "../../../components/Error/index";
import { InsightsTrendLineChart } from "../../../components/chart/insightsTrendLineChart";
import {
  InsightsTrendBarChart,
  InsightsTrendPieChart,
  InsightsMtdLmtdLineChart,
  INSIGHTS_MTD_COLOR,
  INSIGHTS_LMTD_COLOR,
} from "../../../components/chart/insightsCharts";
import {
  INSIGHTS_SECTIONS,
  INSIGHTS_METRIC_TYPES,
  parseDailyTrends,
  parseWeeklyTrends,
  parseMonthlyTrends,
  parseMtdLmtdTrends,
} from "../../../utils/rechargeSalesInsights";

/** Uniform card height for all insight chart cards. */
const INSIGHTS_CARD_HEIGHT_PX = 500;
const INSIGHTS_CARD_HEADER_PX = 100;

const insightCardShellStyle = {
  height: INSIGHTS_CARD_HEIGHT_PX,
  minHeight: INSIGHTS_CARD_HEIGHT_PX,
  width: "100%",
  boxSizing: "border-box",
};

/** Formats a number with comma separators, capped at 2 decimal places. */
function formatKpiValue(val) {
  const n = Number(val);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

/** Parses "dd-mm-yyyy" → { day: "14", month: "Apr", dayName: "Mon" } */
function parsePeakDate(raw) {
  if (!raw) return null;
  const parts = String(raw).split("-");
  if (parts.length < 3) return null;
  const [dd, mm, yyyy] = parts;
  const d = new Date(`${yyyy}-${mm}-${dd}`);
  if (isNaN(d.getTime())) return null;
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${parseInt(dd, 10)}-${monthNames[d.getMonth()]} (${dayNames[d.getDay()]})`;
}

/** Returns current month abbreviation e.g. "Jun" */
function currentMonthAbbr() {
  return ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][new Date().getMonth()];
}

const TILE_LABEL_STYLE = {
  fontSize: "0.68rem",
  color: "#64748b",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  lineHeight: 1.25,
};
const TILE_VALUE_STYLE = {
  fontSize: "1.15rem",
  fontWeight: 700,
  color: "#1e293b",
  lineHeight: 1.3,
  wordBreak: "break-word",
};
const TILE_SUB_STYLE_BASE = {
  fontSize: "0.72rem",
  fontWeight: 600,
  lineHeight: 1.25,
};

function KpiTile({ label, value, subText, subColor }) {
  return (
    <div className="insights-kpi-tile">
      <span className="insights-kpi-tile-label" style={TILE_LABEL_STYLE}>
        {label}
      </span>
      <span className="insights-kpi-tile-value" style={TILE_VALUE_STYLE}>
        {value}
      </span>
      {subText && (
        <span
          className="insights-kpi-tile-sub"
          style={{ ...TILE_SUB_STYLE_BASE, color: subColor || "#64748b" }}
        >
          {subText}
        </span>
      )}
    </div>
  );
}

/**
 * Four KPI summary tiles for the Daily Trends card.
 * Reads summary fields from the daily API response object:
 * mtd_sales, mtd_vs_lmtd_growth_rate, l7d_sales, l7d_growth_rate,
 * daily_sales_avg, peak_sales_value, peak_sales_date.
 */
function DailyKpiTiles({ data, mtdGrowthRate }) {
  // MTD tile — growth comes from MTD/LMTD data block (total_mtd vs total_lmtd)
  const mtdSales = data?.mtd_sales;
  // Prefer pre-computed prop; fall back to inline field if backend ever adds it
  const rawMtdGrowth = mtdGrowthRate ?? data?.mtd_vs_lmtd_growth_rate;
  const mtdGrowth = Number(rawMtdGrowth);
  const hasMtdGrowth = rawMtdGrowth != null && !Number.isNaN(mtdGrowth);
  const mtdSubText = hasMtdGrowth
    ? `${mtdGrowth >= 0 ? "▲" : "▼"} ${Math.abs(mtdGrowth).toFixed(1)}% vs LMTD`
    : null;
  const mtdSubColor = hasMtdGrowth ? (mtdGrowth >= 0 ? "#16a34a" : "#dc2626") : undefined;

  // L7D tile
  const l7dSales = data?.l7d_sales;
  const l7dGrowth = Number(data?.l7d_growth_rate);
  const hasL7dGrowth = !Number.isNaN(l7dGrowth) && data?.l7d_growth_rate != null;
  const l7dSubText = hasL7dGrowth
    ? `${l7dGrowth >= 0 ? "▲" : "▼"} ${Math.abs(l7dGrowth).toFixed(1)}% vs prior week`
    : null;
  const l7dSubColor = hasL7dGrowth ? (l7dGrowth >= 0 ? "#16a34a" : "#dc2626") : undefined;

  // Daily avg tile
  const dailyAvg = data?.daily_sales_avg;
  const avgLabel = `Daily Avg (${currentMonthAbbr()})`;

  // Peak day tile
  const peakValue = data?.peak_sales_value;
  const peakDateFormatted = parsePeakDate(data?.peak_sales_date);

  return (
    <div className="insights-kpi-tiles">
      <KpiTile
        label="MTD Sales"
        value={mtdSales != null ? formatKpiValue(mtdSales) : "—"}
        subText={mtdSubText}
        subColor={mtdSubColor}
      />
      <KpiTile
        label="Last 7 Days"
        value={l7dSales != null ? formatKpiValue(l7dSales) : "—"}
        subText={l7dSubText}
        subColor={l7dSubColor}
      />
      <KpiTile
        label={avgLabel}
        value={dailyAvg != null ? formatKpiValue(dailyAvg) : "—"}
      />
      <KpiTile
        label="Peak Day"
        value={peakValue != null ? formatKpiValue(peakValue) : "—"}
        subText={peakDateFormatted}
        subColor="#64748b"
      />
    </div>
  );
}

function InsightBlock({ title, subtitle, loading, error, data, metricKey, mtdGrowthRate }) {
  const [mtdLmtdView, setMtdLmtdView] = useState("MTD");

  const titleBlock =
    metricKey !== "mtdLmtd" ? (
      <div className="d-flex flex-column min-w-0">
        <span
          className="font-weight-bold text-uppercase"
          style={{ fontSize: "0.85rem" }}
        >
          {title}
        </span>
        {subtitle ? (
          <span className="text-muted small mt-1">{subtitle}</span>
        ) : null}
      </div>
    ) : null;

  const mtdLmtdHeaderPills =
    metricKey === "mtdLmtd" ? (
      <div className="d-flex gap-1">
        <button
          type="button"
          className="rounded-pill border-0 px-2 py-1"
          style={{
            fontSize: "0.75rem",
            backgroundColor:
              mtdLmtdView === "MTD" ? INSIGHTS_MTD_COLOR : "transparent",
            color: mtdLmtdView === "MTD" ? "#fff" : INSIGHTS_MTD_COLOR,
            border:
              mtdLmtdView === "LMTD"
                ? `1px solid ${INSIGHTS_MTD_COLOR}`
                : "none",
          }}
          onClick={() => setMtdLmtdView("MTD")}
        >
          MTD
        </button>
        <button
          type="button"
          className="rounded-pill border-0 px-2 py-1"
          style={{
            fontSize: "0.75rem",
            backgroundColor:
              mtdLmtdView === "LMTD" ? INSIGHTS_LMTD_COLOR : "transparent",
            color: mtdLmtdView === "LMTD" ? "#fff" : INSIGHTS_LMTD_COLOR,
            border:
              mtdLmtdView === "MTD"
                ? `1px solid ${INSIGHTS_LMTD_COLOR}`
                : "none",
          }}
          onClick={() => setMtdLmtdView("LMTD")}
        >
          LMTD
        </button>
      </div>
    ) : null;

  if (loading) {
    return (
      <div
        className="rounded border bg-white p-3 shadow-sm d-flex flex-column"
        style={insightCardShellStyle}
      >
        <div
          className="d-flex justify-content-between align-items-start flex-shrink-0"
          style={{ height: INSIGHTS_CARD_HEADER_PX }}
        >
          {titleBlock}
          {mtdLmtdHeaderPills ? (
            <span className="ml-auto">{mtdLmtdHeaderPills}</span>
          ) : null}
        </div>
        <div
          className="d-flex align-items-center justify-content-center flex-grow-1"
          style={{ minHeight: 0 }}
        >
          <Spinner />
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div
        className="rounded border bg-white p-3 shadow-sm d-flex flex-column"
        style={insightCardShellStyle}
      >
        <div
          className="d-flex justify-content-between align-items-start flex-shrink-0"
          style={{ height: INSIGHTS_CARD_HEADER_PX }}
        >
          {titleBlock}
          {mtdLmtdHeaderPills ? (
            <span className="ml-auto">{mtdLmtdHeaderPills}</span>
          ) : null}
        </div>
        <div
          className="flex-grow-1 d-flex align-items-start pt-2"
          style={{ minHeight: 0, overflow: "auto" }}
        >
          <Error error={error} />
        </div>
      </div>
    );
  }

  const content = data != null ? (Array.isArray(data) ? data : [data]) : [];
  const hasTableData = content.length > 0;
  const hideKeys = ["start_date", "end_date", "startDate", "endDate"];
  const filterDisplayKeys = (obj) =>
    typeof obj === "object" && obj !== null
      ? Object.entries(obj).filter(([k]) => !hideKeys.includes(k))
      : [];

  let chartEl = null;

  if (metricKey === "daily") {
    const { labels, values } = parseDailyTrends(data);
    if (labels.length && values.length) {
      chartEl = (
        <InsightsTrendLineChart
          labels={labels}
          values={values}
          datasetLabel={title}
        />
      );
    }
  } else if (metricKey === "weekly") {
    const { labels, values } = parseWeeklyTrends(data);
    if (labels.length && values.length) {
      chartEl = (
        <InsightsTrendBarChart
          labels={labels}
          values={values}
          datasetLabel={title}
          weeklyAvg={data?.weekly_avg ?? 0}
        />
      );
    }
  } else if (metricKey === "monthly") {
    const { labels, values } = parseMonthlyTrends(data);
    if (labels.length && values.length) {
      chartEl = (
        <InsightsTrendPieChart
          labels={labels}
          values={values}
          datasetLabel={title}
        />
      );
    }
  } else if (metricKey === "mtdLmtd") {
    const { labels, mtdValues, lmtdValues } = parseMtdLmtdTrends(data);
    if (labels.length) {
      chartEl = (
        <InsightsMtdLmtdLineChart
          labels={labels}
          mtdValues={mtdValues}
          lmtdValues={lmtdValues}
          emphasis={mtdLmtdView}
        />
      );
    }
  }

  // Compact "no data" card — avoid 500px empty space when API returns empty
  const hasNoData = !chartEl && !hasTableData;
  if (hasNoData) {
    return (
      <div
        className="rounded border bg-white p-3 shadow-sm d-flex flex-column justify-content-center"
        style={{ width: "100%", boxSizing: "border-box", minHeight: 80 }}
      >
        <div className="d-flex align-items-center justify-content-between mb-1">
          {titleBlock}
        </div>
        <p className="text-muted mb-0 small">No data available</p>
      </div>
    );
  }

  return (
    <div
      className="rounded border bg-white p-3 shadow-sm d-flex flex-column insights-chart-card"
      style={insightCardShellStyle}
    >
      <div className="d-flex justify-content-between align-items-start flex-wrap w-100 insights-chart-card-header">
        {titleBlock}
        {mtdLmtdHeaderPills ? (
          <span className="ml-auto">{mtdLmtdHeaderPills}</span>
        ) : null}
      </div>
      {metricKey === "daily" && <DailyKpiTiles data={data} mtdGrowthRate={mtdGrowthRate} />}
      <div
        className="mdnchart position-relative flex-grow-1 w-100 align-self-stretch insights-chart-area"
        style={{
          flex: "1 1 auto",
          minHeight: 240,
        }}
      >
        {chartEl ? (
          <div
            className="position-absolute"
            style={{
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: "100%",
              height: "100%",
            }}
          >
            <div
              style={{ width: "100%", height: "100%", position: "relative" }}
            >
              {chartEl}
            </div>
          </div>
        ) : hasTableData ? (
          <div className="table-responsive h-100" style={{ overflow: "auto" }}>
            <table className="table table-sm table-bordered mb-0">
              <tbody>
                {content.map((row, idx) =>
                  typeof row === "object" && row !== null ? (
                    <tr key={idx}>
                      {filterDisplayKeys(row).map(([k, v]) => (
                        <td key={k}>
                          <strong>{k}:</strong> {String(v)}
                        </td>
                      ))}
                    </tr>
                  ) : (
                    <tr key={idx}>
                      <td>{String(row)}</td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function InsightsTitleRow({ insightsContextLabel, expanded, onToggle }) {
  return (
    <div
      className={`insights-title-wrap subtitlebox w-100 ${
        expanded ? "mb-3" : "mb-2"
      }`}
    >
      <div className="d-flex align-items-center justify-content-between w-100 flex-nowrap">
        <div className="d-flex align-items-center min-w-0 pr-2">
          <span
            style={{
              display: "inline-block",
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: "#ef4444",
              marginRight: 8,
              flexShrink: 0,
            }}
          />
          <span className="subtitleboxChild font-weight-bold flex-shrink-0">
            INSIGHTS
          </span>
        </div>
        <button
          type="button"
          className="insights-collapse-btn"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={expanded ? "Collapse insights" : "Expand insights"}
          title={expanded ? "Collapse insights" : "Expand insights"}
        >
          <i
            className={`fa fa-angle-${expanded ? "up" : "down"}`}
            aria-hidden
          />
        </button>
      </div>
      {insightsContextLabel != null && insightsContextLabel !== "" ? (
        <div className="insights-context-line text-muted mt-2 mb-0">
          <span style={{ fontSize: "0.9rem" }}>
            Showing insights for: {insightsContextLabel}
          </span>
        </div>
      ) : null}
    </div>
  );
}

export default function RechargeSalesInsightsView({
  rechargeInsights,
  filterValue,
  section,
  insightsContextLabel,
}) {
  const [insightsExpanded, setInsightsExpanded] = useState(true);
  const toggleInsights = () => setInsightsExpanded((prev) => !prev);

  const {
    loading,
    error: globalError,
    data,
    loadingKeys = {},
  } = rechargeInsights || {};
  const hasData = data && Object.keys(data).length > 0;
  const singleSection = section != null && section !== "";

  if (globalError && !hasData) {
    return (
      <div className="mt-3">
        <InsightsTitleRow
          insightsContextLabel={insightsContextLabel}
          expanded={insightsExpanded}
          onToggle={toggleInsights}
        />
        {insightsExpanded ? <Error error={globalError} /> : null}
      </div>
    );
  }

  const renderOneSection = (sectionKey) => {
    // Compute MTD vs LMTD growth rate from the mtdLmtd block (total_mtd / total_lmtd)
    const mtdLmtdData = data && data[`${sectionKey}_mtdLmtd`];
    const totalMtd = Number(mtdLmtdData?.total_mtd);
    const totalLmtd = Number(mtdLmtdData?.total_lmtd);
    const mtdGrowthRate =
      mtdLmtdData != null && !Number.isNaN(totalMtd) && !Number.isNaN(totalLmtd) && totalLmtd > 0
        ? parseFloat(((totalMtd - totalLmtd) / totalLmtd * 100).toFixed(1))
        : null;

    return (
    <>
      <div
        className="insights-scroll-hint"
        role="note"
        aria-label="More charts available by swiping horizontally"
      >
        <i
          className="fa fa-arrow-left insights-scroll-hint-arrow insights-scroll-hint-arrow-left"
          aria-hidden
        />
        <span>Swipe sideways for more charts</span>
        <i
          className="fa fa-arrow-right insights-scroll-hint-arrow insights-scroll-hint-arrow-right"
          aria-hidden
        />
      </div>
      <div className="row insights-charts-row">
        {INSIGHTS_METRIC_TYPES.map(
          ({
            key: metricKey,
            label: metricLabel,
            subtitle: metricSubtitle,
          }) => {
            const dataKey = `${sectionKey}_${metricKey}`;
            const blockData = data && data[dataKey];
            return (
              <div
                key={dataKey}
                className="insights-chart-col col-12 col-md-6 mb-3 d-flex align-items-stretch"
              >
                <InsightBlock
                  title={metricLabel.toUpperCase()}
                  subtitle={metricSubtitle}
                  loading={loadingKeys[dataKey]}
                  data={blockData}
                  metricKey={metricKey}
                  mtdGrowthRate={metricKey === "daily" ? mtdGrowthRate : undefined}
                />
              </div>
            );
          },
        )}
      </div>
    </>
  );
  };

  return (
    <div className="mt-3" data-wow-delay=".6s" data-wow-duration="0.7s">
      <InsightsTitleRow
        insightsContextLabel={insightsContextLabel}
        expanded={insightsExpanded}
        onToggle={toggleInsights}
      />
      {insightsExpanded ? (
        !singleSection && loading && !hasData ? (
          <Spinner />
        ) : singleSection ? (
          <div className="tabcontbox">{renderOneSection(section)}</div>
        ) : (
          <div className="tabcontbox">
            {INSIGHTS_SECTIONS.map(
              ({ key: sectionKey, label: sectionLabel }) => (
                <div key={sectionKey} className="mb-4">
                  <h5 className="mb-3 border-bottom pb-2">{sectionLabel}</h5>
                  {renderOneSection(sectionKey)}
                </div>
              ),
            )}
          </div>
        )
      ) : null}
    </div>
  );
}
